import { useState } from "react";
import {
  servicesMegaMenuData,
  industriesMegaMenuData,
  solutionsMegaMenuData,
  technologiesMegaMenuData,
  portfolioDropdownData,
  companyDropdownData,
  companyReviewsData,
} from "@/mocks/headerData";

type MegaMenuType = "services" | "industries" | "solutions" | "technologies" | "portfolio" | "company";

interface MegaMenuPanelProps {
  type: MegaMenuType;
}

/* ── Accent palette ───────────────────────────────────────────── */
const categoryAccents = [
  { iconBg: "bg-blue-50", iconText: "text-blue-600", hoverText: "hover:text-blue-600", hoverBg: "hover:bg-blue-50/50", hoverBorder: "hover:border-blue-200", chevronText: "group-hover/link:text-blue-500", ringColor: "focus-visible:ring-blue-400" },
  { iconBg: "bg-violet-50", iconText: "text-violet-600", hoverText: "hover:text-violet-600", hoverBg: "hover:bg-violet-50/50", hoverBorder: "hover:border-violet-200", chevronText: "group-hover/link:text-violet-500", ringColor: "focus-visible:ring-violet-400" },
  { iconBg: "bg-fuchsia-50", iconText: "text-fuchsia-600", hoverText: "hover:text-fuchsia-600", hoverBg: "hover:bg-fuchsia-50/50", hoverBorder: "hover:border-fuchsia-200", chevronText: "group-hover/link:text-fuchsia-500", ringColor: "focus-visible:ring-fuchsia-400" },
  { iconBg: "bg-orange-50", iconText: "text-orange-600", hoverText: "hover:text-orange-600", hoverBg: "hover:bg-orange-50/50", hoverBorder: "hover:border-orange-200", chevronText: "group-hover/link:text-orange-500", ringColor: "focus-visible:ring-orange-400" },
  { iconBg: "bg-cyan-50", iconText: "text-cyan-600", hoverText: "hover:text-cyan-600", hoverBg: "hover:bg-cyan-50/50", hoverBorder: "hover:border-cyan-200", chevronText: "group-hover/link:text-cyan-500", ringColor: "focus-visible:ring-cyan-400" },
  { iconBg: "bg-rose-50", iconText: "text-rose-600", hoverText: "hover:text-rose-600", hoverBg: "hover:bg-rose-50/50", hoverBorder: "hover:border-rose-200", chevronText: "group-hover/link:text-rose-500", ringColor: "focus-visible:ring-rose-400" },
  { iconBg: "bg-emerald-50", iconText: "text-emerald-600", hoverText: "hover:text-emerald-600", hoverBg: "hover:bg-emerald-50/50", hoverBorder: "hover:border-emerald-200", chevronText: "group-hover/link:text-emerald-500", ringColor: "focus-visible:ring-emerald-400" },
  { iconBg: "bg-amber-50", iconText: "text-amber-600", hoverText: "hover:text-amber-600", hoverBg: "hover:bg-amber-50/50", hoverBorder: "hover:border-amber-200", chevronText: "group-hover/link:text-amber-500", ringColor: "focus-visible:ring-amber-400" },
];

function getAccent(index: number) {
  return categoryAccents[index % categoryAccents.length];
}

/* ── Icon mapping ─────────────────────────────────────────────── */
const serviceIconMap: Record<string, string> = {
  "Laravel & Backend Development": "ri-server-line",
  "Web Development": "ri-global-line",
  "Mobile App Development": "ri-smartphone-line",
  "AI & AI Development": "ri-sparkling-line",
  "API & SaaS Development": "ri-plug-line",
  "Cloud & DevOps": "ri-cloud-line",
  "UI/UX & Product Design": "ri-pencil-ruler-line",
  "Maintenance & Support": "ri-tools-line",
};

const industryIconMap: Record<string, string> = {
  Healthcare: "ri-hospital-line",
  FinTech: "ri-bank-line",
  "E-commerce": "ri-shopping-bag-2-line",
  Education: "ri-graduation-cap-line",
  "Real Estate": "ri-building-line",
  Logistics: "ri-truck-line",
  Travel: "ri-plane-line",
  SaaS: "ri-cloud-line",
  Enterprise: "ri-building-4-line",
  "Media & Entertainment": "ri-movie-line",
};

const solutionIconMap: Record<string, string> = {
  "For Startups": "ri-rocket-line",
  "For Businesses": "ri-building-4-line",
  "For Enterprises": "ri-bar-chart-box-line",
  "For Product Companies": "ri-box-3-line",
  "AI Solutions": "ri-bard-line",
};

const portfolioIconMap: Record<string, string> = {
  "All Projects": "ri-folder-open-line",
  "Laravel Projects": "ri-code-s-slash-line",
  "SaaS Projects": "ri-stack-line",
  "Web Applications": "ri-window-line",
  "Mobile Apps": "ri-smartphone-line",
  "API Projects": "ri-exchange-line",
  "AI Projects": "ri-sparkling-line",
  "Enterprise Solutions": "ri-building-4-line",
};

/* ── Service item icon resolver (keyword based) ───────────────── */
function getServiceItemIcon(label: string, fallback: string): string {
  const l = label.toLowerCase();
  if (l.includes("ci/cd")) return "ri-git-branch-line";
  if (l.includes("hire")) return "ri-user-add-line";
  if (l.includes("chatgpt") || l.includes("claude") || l.includes("chatbot") || l.includes("assistant")) return "ri-sparkling-line";
  if (l.includes("ai")) return "ri-sparkling-line";
  if (l.includes("workflow") || l.includes("automation")) return "ri-flow-chart";
  if (l.includes("api")) return "ri-plug-line";
  if (l.includes("saas")) return "ri-stack-line";
  if (l.includes("microservice")) return "ri-node-tree";
  if (l.includes("tenant")) return "ri-group-line";
  if (l.includes("subscription")) return "ri-repeat-line";
  if (l.includes("enterprise")) return "ri-building-4-line";
  if (l.includes("crm")) return "ri-contacts-line";
  if (l.includes("erp")) return "ri-bar-chart-box-line";
  if (l.includes("admin") || l.includes("dashboard")) return "ri-dashboard-line";
  if (l.includes("migration")) return "ri-magic-line";
  if (l.includes("performance") || l.includes("optimization")) return "ri-speed-up-line";
  if (l.includes("maintenance") || l.includes("support")) return "ri-tools-line";
  if (l.includes("security")) return "ri-shield-check-line";
  if (l.includes("bug")) return "ri-bug-line";
  if (l.includes("update")) return "ri-refresh-line";
  if (l.includes("flutter") || l.includes("cross-platform")) return "ri-smartphone-line";
  if (l.includes("android")) return "ri-android-line";
  if (l.includes("ios")) return "ri-apple-line";
  if (l.includes("push") || l.includes("notification")) return "ri-notification-3-line";
  if (l.includes("mobile")) return "ri-smartphone-line";
  if (l.includes("vue")) return "ri-vuejs-line";
  if (l.includes("react")) return "ri-reactjs-line";
  if (l.includes("php")) return "ri-code-box-line";
  if (l.includes("laravel")) return "ri-code-s-slash-line";
  if (l.includes("aws")) return "ri-cloud-line";
  if (l.includes("cloud") || l.includes("deployment")) return "ri-cloud-line";
  if (l.includes("docker")) return "ri-instance-line";
  if (l.includes("cloudflare")) return "ri-shield-flash-line";
  if (l.includes("server")) return "ri-server-line";
  if (l.includes("monitoring")) return "ri-line-chart-line";
  if (l.includes("ui/ux") || l.includes("design")) return "ri-palette-line";
  if (l.includes("web")) return "ri-global-line";
  if (l.includes("custom")) return "ri-code-s-slash-line";
  return fallback;
}

/* ── Reusable Category Card ───────────────────────────────────── */
function CategoryCard({
  title,
  icon,
  items,
  accentIndex,
  showDesc = false,
  desc = "",
}: {
  title: string;
  icon: string;
  items: { label: string; href: string }[];
  accentIndex: number;
  showDesc?: boolean;
  desc?: string;
}) {
  const a = getAccent(accentIndex);

  return (
    <div
      className={`mega-card group rounded-xl bg-white border border-gray-100/80 p-4 ${a.hoverBorder} hover:shadow-lg focus-within:shadow-lg focus-within:border-gray-200`}
      tabIndex={-1}
      role="group"
      aria-label={title}
    >
      {/* Header row */}
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${a.iconBg} flex items-center justify-center shrink-0`}>
          <i className={`${icon} text-lg ${a.iconText}`} />
        </div>
        <div className="min-w-0 pt-1">
          <h4 className="text-sm font-semibold text-[#1B2A5B] leading-tight">
            {title}
          </h4>
          {showDesc && desc && (
            <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">{desc}</p>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-gray-100 mb-2" />

      {/* Sub-links */}
      <div className="flex flex-col">
        {items.map((item) => (
          <a
            key={item.label}
            href={item.href}
            role="menuitem"
            className={`group/link flex items-center gap-1.5 py-1 px-2 -mx-2 text-sm text-foreground-600 rounded-md ${a.hoverText} ${a.hoverBg} transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 ${a.ringColor} focus-visible:ring-offset-1`}
          >
            <span className="w-4 h-4 flex items-center justify-center shrink-0">
              <i className={`ri-arrow-right-s-line text-xs text-foreground-300 ${a.chevronText} transition-all duration-200`} />
            </span>
            <span className="group-hover/link:translate-x-0.5 transition-transform duration-200">
              {item.label}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

/* ── Modern Service Card (services menu only) ─────────────────── */
function ServiceCard({
  title,
  icon,
  items,
  accentIndex,
}: {
  title: string;
  icon: string;
  items: { label: string; href: string }[];
  accentIndex: number;
}) {
  const a = getAccent(accentIndex);

  return (
    <div
      className={`mega-card group/card rounded-2xl bg-white border border-gray-100 p-4 ${a.hoverBorder} hover:bg-gray-50/50 transition-all duration-300`}
      tabIndex={-1}
      role="group"
      aria-label={title}
    >
      {/* Header row */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className={`w-11 h-11 rounded-xl ${a.iconBg} flex items-center justify-center shrink-0 transition-transform duration-300 group-hover/card:scale-105`}
        >
          <i className={`${icon} text-xl ${a.iconText}`} />
        </div>
        <div className="min-w-0">
          <h4 className="text-base font-bold text-[#1B2A5B] leading-tight">
            {title}
          </h4>
          <p className="text-xs text-foreground-400 mt-0.5">{items.length} Services</p>
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-gray-200 via-gray-100 to-transparent mb-2.5" />

      {/* Sub-links */}
      <div className="flex flex-col gap-0.5">
        {items.map((item) => (
          <a
            key={item.label}
            href={item.href}
            role="menuitem"
            className={`group/link flex items-center gap-2.5 py-1.5 px-2 -mx-2 rounded-lg text-[15px] font-medium text-foreground-600 ${a.hoverText} ${a.hoverBg} transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 ${a.ringColor} focus-visible:ring-offset-1`}
          >
            <span className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className={`${getServiceItemIcon(item.label, icon)} text-[15px] ${a.iconText}`} />
            </span>
            <span className="flex-1 leading-snug group-hover/link:translate-x-0.5 transition-transform duration-200">
              {item.label}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

/* ── Right-side Promo Panel ───────────────────────────────────── */
function PromoPanel() {
  return (
    <div className="flex flex-col justify-between h-full rounded-xl bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] p-6 text-white">
      <div>
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#7C3AED] flex items-center justify-center mb-4">
          <i className="ri-question-line text-white text-xl" />
        </div>
        <h3 className="text-lg font-bold mb-2">Not sure what you need?</h3>
        <p className="text-sm text-foreground-300 mb-6 leading-relaxed">
          Let&apos;s talk about your project and find the right solution.
        </p>
      </div>
      <a
        href="/contact-us"
        className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-lg hover:brightness-110 transition-all duration-300 whitespace-nowrap"
      >
        Let&apos;s Talk
        <i className="ri-arrow-right-line text-xs" />
      </a>
    </div>
  );
}

/* ── Services ─────────────────────────────────────────────────── */
function ServicesMegaMenu() {
  return (
    <div className="flex gap-5">
      <div className="flex-1 pr-1">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3.5">
          {servicesMegaMenuData.map((cat, i) => (
            <ServiceCard
              key={cat.title}
              title={cat.title}
              icon={serviceIconMap[cat.title] || cat.icon}
              items={cat.items}
              accentIndex={i}
            />
          ))}
        </div>
      </div>
      <div className="w-[260px] shrink-0 hidden xl:block">
        <PromoPanel />
      </div>
    </div>
  );
}

/* ── Industries ───────────────────────────────────────────────── */
function IndustriesMegaMenu() {
  const { items, highlight } = industriesMegaMenuData;
  return (
    <div className="flex gap-5">
      <div className="flex-1 pr-1">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-4 gap-4">
          {items.map((industry, i) => (
            <CategoryCard
              key={industry.name}
              title={industry.name}
              icon={industryIconMap[industry.name] || industry.icon}
              items={[{ label: "Explore", href: industry.href }]}
              accentIndex={i}
              showDesc
              desc={industry.desc}
            />
          ))}
        </div>
      </div>
      <div className="w-[280px] shrink-0 hidden xl:block">
        <div className="flex flex-col h-full rounded-xl bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] p-6 text-white">
          <div className="relative w-full h-32 rounded-xl overflow-hidden mb-4">
            <img
              src={highlight.image}
              alt="Technology solutions"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent" />
          </div>
          <h4 className="text-base font-bold text-white mb-2">{highlight.title}</h4>
          <p className="text-sm text-foreground-300 mb-4 leading-relaxed">{highlight.desc}</p>
          <div className="grid grid-cols-3 gap-3 mb-5">
            {highlight.stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-lg font-bold gradient-text">{stat.value}</div>
                <div className="text-xs text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
          <a
            href={highlight.ctaHref}
            className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-lg hover:brightness-110 transition-all duration-300 whitespace-nowrap"
          >
            {highlight.cta}
            <i className="ri-arrow-right-line text-xs" />
          </a>
        </div>
      </div>
    </div>
  );
}

/* ── Solutions ────────────────────────────────────────────────── */
function SolutionsMegaMenu() {
  return (
    <div className="pr-1">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {solutionsMegaMenuData.map((group, i) => (
          <CategoryCard
            key={group.title}
            title={group.title}
            icon={solutionIconMap[group.title] || group.icon}
            items={group.items}
            accentIndex={i}
          />
        ))}
      </div>
    </div>
  );
}

/* ── Technologies ─────────────────────────────────────────────── */
function TechnologiesMegaMenu() {
  return (
    <div className="pr-1">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {technologiesMegaMenuData.map((group, i) => {
          const a = getAccent(i);
          return (
            <div
              key={group.name}
              className={`mega-card group rounded-xl bg-white border border-gray-100/80 p-4 ${a.hoverBorder} hover:shadow-lg focus-within:shadow-lg focus-within:border-gray-200`}
              tabIndex={-1}
              role="group"
              aria-label={group.name}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg ${a.iconBg} flex items-center justify-center shrink-0`}>
                  <i className={`ri-code-box-line text-lg ${a.iconText}`} />
                </div>
                <h4 className="text-sm font-semibold text-[#1B2A5B]">{group.name}</h4>
              </div>
              <div className="h-px bg-gray-100 mb-2" />
              <div className="flex flex-col gap-2">
                {group.items.map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center gap-2.5 py-1 px-2 -mx-2 rounded-md hover:bg-gray-50/50 transition-colors"
                  >
                    {item.logo ? (
                      <img
                        src={item.logo}
                        alt={item.name}
                        className={`w-5 h-5 object-contain ${item.featured ? "ring-2 ring-blue-400/30 rounded-sm" : ""}`}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="w-5 h-5 rounded bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center">
                        <span className="text-[8px] font-bold text-white">{item.name.charAt(0)}</span>
                      </div>
                    )}
                    <span className={`text-sm ${item.featured ? "font-semibold text-blue-600" : "text-foreground-600"}`}>
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Portfolio ──────────────────────────────────────────────────── */
function PortfolioMegaMenu() {
  return (
    <div className="pr-1">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {portfolioDropdownData.items.map((item, i) => (
          <CategoryCard
            key={item.label}
            title={item.label}
            icon={portfolioIconMap[item.label] || "ri-folder-line"}
            items={[{ label: "View", href: item.href }]}
            accentIndex={i}
          />
        ))}
      </div>
    </div>
  );
}

/* ── Company ──────────────────────────────────────────────────── */
function ReviewLogo({ name, logo }: { name: string; logo: string }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span className="text-xs font-bold text-foreground-700 truncate">{name}</span>
    );
  }

  return (
    <img
      src={logo}
      alt={`${name} reviews`}
      title={`${name} reviews`}
      onError={() => setFailed(true)}
      className="h-5 w-auto max-w-[96px] object-contain object-left"
    />
  );
}

function CompanyReviews() {
  return (
    <div className="flex flex-col h-full rounded-2xl bg-background-100 border border-background-200/60 p-6">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-accent-50 text-accent-500">
          <i className="ri-star-smile-line text-lg" />
        </span>
        <h3 className="text-base font-bold text-foreground-950 font-heading">Loved by Our People &amp; Partners</h3>
      </div>
      <p className="text-xs text-foreground-500 leading-relaxed mb-5">
        Our work and workplace, rated across leading platforms.
      </p>

      <div className="grid grid-cols-2 gap-3">
        {companyReviewsData.platforms.map((platform) => (
          <div
            key={platform.name}
            className="rounded-xl bg-background-50 border border-background-200/60 p-3"
          >
            <div className="flex items-center gap-1.5 text-accent-500 mb-2">
              <i className="ri-star-fill text-xs" />
              <span className="text-sm font-bold text-foreground-950">{platform.rating.toFixed(1)}</span>
            </div>
            <div className="h-5 flex items-center">
              <ReviewLogo name={platform.name} logo={platform.logo} />
            </div>
          </div>
        ))}
      </div>

      <a
        href="/contact-us"
        className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap group"
      >
        Work with us
        <i className="ri-arrow-right-line text-xs transition-transform duration-200 group-hover:translate-x-1" />
      </a>
    </div>
  );
}

function CompanyMegaMenu() {
  return (
    <div className="flex gap-5">
      <div className="flex-1 pr-1">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3.5">
          {companyDropdownData.items.map((item, i) => {
            const a = getAccent(i);
            return (
              <a
                key={item.label}
                href={item.href}
                role="menuitem"
                className={`mega-card group rounded-2xl bg-white border border-gray-100 p-4 ${a.hoverBorder} hover:bg-gray-50/50 transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 ${a.ringColor} focus-visible:ring-offset-1`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-11 h-11 rounded-xl ${a.iconBg} flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105`}
                  >
                    <i className={`${item.icon} text-xl ${a.iconText}`} />
                  </div>
                  <div className="min-w-0 pt-0.5">
                    <h4 className="text-base font-bold text-[#1B2A5B] leading-tight flex items-center gap-1.5">
                      {item.label}
                      <i className={`ri-arrow-right-up-line text-xs ${a.iconText} opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200`} />
                    </h4>
                    <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
      <div className="w-[300px] shrink-0 hidden xl:block">
        <CompanyReviews />
      </div>
    </div>
  );
}

/* ── Main Export ──────────────────────────────────────────────── */
export default function MegaMenuPanel({ type }: MegaMenuPanelProps) {
  switch (type) {
    case "services":
      return <ServicesMegaMenu />;
    case "industries":
      return <IndustriesMegaMenu />;
    case "solutions":
      return <SolutionsMegaMenu />;
    case "technologies":
      return <TechnologiesMegaMenu />;
    case "portfolio":
      return <PortfolioMegaMenu />;
    case "company":
      return <CompanyMegaMenu />;
    default:
      return null;
  }
}

/* Re-export helpers for Header mobile menu */
export { serviceIconMap, industryIconMap, solutionIconMap, portfolioIconMap, getAccent };